import { useRef, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'

function offsetDay(day: string, offset: number) {
  const date = new Date(`${day}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() + offset)
  return date.toISOString().slice(0, 10)
}

export default function TestingDatePicker({ day, today, earliestDay, onChange }: { day: string; today: string; earliestDay: string; onChange: (day: string) => void }) {
  const calendar = useRef<HTMLDetailsElement>(null)
  const [month, setMonth] = useState(day.slice(0, 7))
  const first = `${month}-01`
  const start = offsetDay(first, -new Date(`${first}T12:00:00Z`).getUTCDay())
  const label = (value: string, options: Intl.DateTimeFormatOptions) => new Date(`${value}T12:00:00Z`).toLocaleDateString(undefined, { ...options, timeZone: 'UTC' })
  function moveMonth(delta: number) {
    const date = new Date(`${first}T12:00:00Z`)
    date.setUTCMonth(date.getUTCMonth() + delta)
    setMonth(date.toISOString().slice(0, 7))
  }
  return <div className="testing-date-picker" aria-label="Testing date">
    <button type="button" aria-label="Previous testing day" disabled={day <= earliestDay} onClick={() => onChange(offsetDay(day, -1))}><ChevronLeft size={16} /></button>
    <details ref={calendar} onToggle={event => { if (event.currentTarget.open) setMonth(day.slice(0, 7)) }} onKeyDown={event => {
      if (event.key === 'Escape' && calendar.current) { calendar.current.open = false; calendar.current.querySelector('summary')?.focus() }
    }}>
      <summary aria-label="Choose testing date"><CalendarDays size={16} /><time dateTime={day}>{label(day, { month: 'short', day: 'numeric', year: 'numeric' })}</time></summary>
      <div className="testing-date-picker__calendar">
        <div className="testing-date-picker__month">
          <button type="button" aria-label="Previous month" disabled={month <= earliestDay.slice(0, 7)} onClick={() => moveMonth(-1)}><ChevronLeft size={16} /></button>
          <strong aria-live="polite">{label(first, { month: 'long', year: 'numeric' })}</strong>
          <button type="button" aria-label="Next month" disabled={month >= today.slice(0, 7)} onClick={() => moveMonth(1)}><ChevronRight size={16} /></button>
        </div>
        <div className="testing-date-picker__days">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(name => <span key={name}>{name}</span>)}
          {Array.from({ length: 42 }, (_, index) => {
            const value = offsetDay(start, index)
            return <button key={value} type="button" disabled={value < earliestDay || value > today} aria-label={label(value, { dateStyle: 'full' })} aria-pressed={value === day} data-outside={value.slice(0, 7) !== month} onClick={() => {
              onChange(value)
              if (calendar.current) { calendar.current.open = false; calendar.current.querySelector('summary')?.focus() }
            }}>{Number(value.slice(8))}</button>
          })}
        </div>
      </div>
    </details>
    <button type="button" aria-label="Next testing day" disabled={day >= today} onClick={() => onChange(offsetDay(day, 1))}><ChevronRight size={16} /></button>
  </div>
}
